import React from 'react';
import PropTypes from 'prop-types';

import Alert from '@decisiv/ui-components/lib/components/Alert';
import Message from '@decisiv/ui-components/lib/components/Message';
import { H3, P } from '@decisiv/ui-components/lib/components/Typography';

import PermissionsMatrix from './PermissionsMatrix';
import ScopeChoice from './ScopeChoice';
import { NOTICES, PERMISSIONS_SECTION } from './copy';
import { Section, SectionHeader, SectionHeading } from './styles';

/**
 * "Permissions & Discoverability": the scope radios and the matrix. The "each
 * account needs a permission" message is stated rather than left as an
 * unexplained greyed-out SAVE CHANGES, as the wizard's step does too.
 */
function PermissionsSection({
  columns,
  disabled,
  errors,
  groups,
  isValid,
  mode,
  notice,
  onCellChange,
  onModeChange,
  showScopeChoice,
}) {
  return (
    <Section>
      <SectionHeader>
        <SectionHeading>
          <H3 style={{ margin: 0 }}>{PERMISSIONS_SECTION.title}</H3>
          <P shade={1} size="small" style={{ margin: 0 }}>
            {PERMISSIONS_SECTION.subtitle}
          </P>
        </SectionHeading>
      </SectionHeader>

      {showScopeChoice && (
        <ScopeChoice disabled={disabled} mode={mode} onChange={onModeChange} />
      )}

      {notice && <Alert intent="warning" title={notice} />}

      <PermissionsMatrix
        columns={columns}
        errors={errors}
        groups={groups}
        onCellChange={onCellChange}
      />

      {!isValid && (
        <Message intent="warning">{NOTICES.permissionsRequired}</Message>
      )}
    </Section>
  );
}

PermissionsSection.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  /** True for a viewer holding user-management:Read but not :Modify. */
  disabled: PropTypes.bool,
  errors: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
  groups: PropTypes.arrayOf(PropTypes.object).isRequired,
  isValid: PropTypes.bool,
  mode: PropTypes.oneOf(['all', 'custom']).isRequired,
  /** Explains a scope switch that could not preserve every account's value. */
  notice: PropTypes.string,
  onCellChange: PropTypes.func.isRequired,
  onModeChange: PropTypes.func.isRequired,
  showScopeChoice: PropTypes.bool,
};

PermissionsSection.defaultProps = {
  disabled: false,
  errors: {},
  isValid: true,
  notice: null,
  showScopeChoice: false,
};

export default PermissionsSection;
